/**
 * How an ingredient id becomes a drawable icon, without the renderer ever
 * learning a recipe.
 *
 * This is the file that keeps phase 8's hardest constraint honest: adding a
 * dish must stay a `recipes.json` edit. So nothing here — and nothing in
 * `icons.ts` — knows that `stewed_carrot` is a stewed carrot. What it knows is
 * the shape of the DAG, which the server now sends in `/api/config`:
 *
 *     chopped_carrot  from { station: 'board', ingredient: 'carrot' }
 *     stewed_carrot   from { station: 'stove', ingredient: 'chopped_carrot',
 *                            container: 'pan' }
 *
 * Walking that backwards gives three facts and nothing else: which **source**
 * ingredient it descends from, whether it has been through a board, and whether
 * it has been through heat. Those are the icon: a shape, cut or whole, raw or
 * cooked.
 *
 * Only *source* ingredients — the ones that come out of a crate — need a drawn
 * shape, and even those have a generative fallback, so a `tomato` crate added
 * to `recipes.json` tomorrow gets a distinct, stable icon with no code change.
 * `SOURCE_SHAPES` below is a glyph hint, not a recipe table: it carries no
 * transform, no dish and no station.
 *
 * Every function here is pure and unit tested. `icons.ts` turns the result into
 * canvas paths and makes no decisions of its own.
 */

import type { ClientConfig, DishSummary, IngredientSummary } from '@overcooked/shared'

/**
 * The eight silhouettes an ingredient can be drawn as.
 *
 * They are deliberately different *shapes*, not different colours, because a
 * colourblind player has to be able to tell a chopped carrot from a chopped
 * onion. Cut and cooked treatments preserve the family, so `bulb` pieces still
 * read as rings and `root` pieces still read as wedges.
 */
type ShapeFamily = 'root' | 'bulb' | 'cap' | 'slab' | 'leaf' | 'pod' | 'grain' | 'sphere'

export const SHAPE_FAMILIES: ShapeFamily[] = [
  'root',
  'bulb',
  'cap',
  'slab',
  'leaf',
  'pod',
  'grain',
  'sphere',
]

/** Which heat an ingredient last saw. Drawn as different surface marks. */
export type Heat = 'none' | 'stove' | 'oven'

export interface IngredientSpec {
  id: string
  /** From `recipes.json`, or a title-cased id when the catalog is unavailable. */
  label: string
  /** The crate ingredient this descends from. Equal to `id` for a source. */
  source: string
  shape: ShapeFamily
  /** Base hue in degrees, and a saturation/lightness pair, for the fill. */
  hue: number
  sat: number
  light: number
  /** Passed through a chopping board: drawn as pieces rather than as one body. */
  cut: boolean
  /** Passed through cooking equipment: darker, with steam. */
  cooked: boolean
  heat: Heat
}

/**
 * Drawn shapes for the ingredients v1 actually ships.
 *
 * A hint table, not a recipe table. If an id is missing the generative path
 * below produces a shape and a hue for it, so this table going stale costs
 * prettiness and never correctness.
 */
const SOURCE_SHAPES: Record<string, { shape: ShapeFamily; hue: number; sat: number; light: number }> =
  {
    meat: { shape: 'slab', hue: 352, sat: 60, light: 52 },
    carrot: { shape: 'root', hue: 24, sat: 88, light: 54 },
    onion: { shape: 'bulb', hue: 290, sat: 22, light: 74 },
    mushroom: { shape: 'cap', hue: 28, sat: 28, light: 62 },
    tomato: { shape: 'sphere', hue: 6, sat: 74, light: 52 },
    lettuce: { shape: 'leaf', hue: 104, sat: 44, light: 46 },
    fish: { shape: 'pod', hue: 200, sat: 30, light: 66 },
    rice: { shape: 'grain', hue: 44, sat: 24, light: 86 },
  }

/** A stable 32-bit hash. Same id, same icon, on every machine and every run. */
export function hashId(id: string): number {
  let hash = 2166136261
  for (let i = 0; i < id.length; i += 1) {
    hash ^= id.charCodeAt(i)
    hash = Math.imul(hash, 16777619) >>> 0
  }
  return hash >>> 0
}

/**
 * A shape and a colour for a source ingredient nobody drew.
 *
 * The hue is spread around the wheel by a golden-angle step rather than by
 * `hash % 360`, so two ingredients added on the same day do not land three
 * degrees apart — which is the failure the previous renderer had.
 */
export function generativeShape(id: string): {
  shape: ShapeFamily
  hue: number
  sat: number
  light: number
} {
  const hash = hashId(id)
  return {
    shape: SHAPE_FAMILIES[hash % SHAPE_FAMILIES.length],
    hue: Math.round(((hash % 997) * 137.508) % 360),
    sat: 58 + (hash % 5) * 6,
    light: 50 + ((hash >> 3) % 4) * 5,
  }
}

export const sourceAppearance = (
  id: string,
): { shape: ShapeFamily; hue: number; sat: number; light: number } =>
  SOURCE_SHAPES[id] ?? generativeShape(id)

/** `chopped_carrot` -> `Chopped carrot`, only when the server told us nothing. */
export function titleCase(id: string): string {
  const words = id.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * The catalog: everything static the renderer needs, and nothing that changes
 * during a round.
 *
 * It is *not* game state, so it does not come through the feed. It is passed
 * into the renderer by whichever page mounted it — the round page and `/replay`
 * pass the identical object, fetched from `/api/config` — which is what keeps
 * `render/` free of any dependency on `net/`.
 */
export interface Catalog {
  ingredient(id: string): IngredientSpec
  dish(id: string): DishSummary | null
  dishes(): DishSummary[]
  /** `pan` -> `Pan`. Falls back to the raw kind. */
  containerLabel(kind: string): string
}

interface CatalogSource {
  ingredients: IngredientSummary[]
  dishes: DishSummary[]
}

/**
 * Resolve one ingredient by walking the DAG backwards.
 *
 * `seen` guards a malformed cycle: the server validates against one at boot,
 * but the renderer must not be the thing that hangs the tab if it ever slips
 * through.
 */
export function resolveSpec(id: string, byId: Map<string, IngredientSummary>): IngredientSpec {
  const summary = byId.get(id)
  let cursor = summary
  let cut = false
  let heat: Heat = 'none'
  const seen = new Set<string>([id])

  while (cursor?.from) {
    const step = cursor.from
    if (step.station === 'board') cut = true
    if (step.station === 'stove') heat = 'stove'
    else if (step.station === 'oven') heat = 'oven'
    if (seen.has(step.ingredient)) break
    seen.add(step.ingredient)
    cursor = byId.get(step.ingredient)
    if (!cursor) break
  }

  const source = cursor?.id ?? id
  const appearance = sourceAppearance(source)
  // `raw: false` in `recipes.json` is the authority on "this has been cooked";
  // the station walk is the fallback for content that omits the flag.
  const cooked = summary ? !summary.raw || heat !== 'none' : heat !== 'none'

  return {
    id,
    label: summary?.label ?? titleCase(id),
    source,
    shape: appearance.shape,
    hue: appearance.hue,
    sat: cooked ? Math.max(20, appearance.sat - 20) : appearance.sat,
    light: cooked ? Math.max(22, appearance.light - 20) : appearance.light,
    cut,
    cooked,
    heat,
  }
}

export function buildCatalog(source: CatalogSource | ClientConfig | null): Catalog {
  const ingredients = source?.ingredients ?? []
  const dishes = source?.dishes ?? []
  const byId = new Map(ingredients.map((i) => [i.id, i]))
  const dishById = new Map(dishes.map((d) => [d.id, d]))
  const cache = new Map<string, IngredientSpec>()

  return {
    ingredient(id: string): IngredientSpec {
      const hit = cache.get(id)
      if (hit) return hit
      const spec = resolveSpec(id, byId)
      cache.set(id, spec)
      return spec
    },
    dish: (id: string) => dishById.get(id) ?? null,
    dishes: () => dishes,
    containerLabel: (kind: string) => titleCase(kind),
  }
}

/** The catalog a page has before `/api/config` answers, and if it never does. */
export const EMPTY_CATALOG: Catalog = buildCatalog(null)
